'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { SelectOption } from 'components/form/Select/useSelect'
import { usePathname } from 'next/navigation'

import { CustomerInfo } from 'app/utils/authentication'
import { SortingInfo } from 'appTypes/mapper'
import { Region } from '@leid/api-locations'
import { LocationModal } from './components/LocationModal'
import { LocationBar } from './components/LocationBar'
import { useBannerCookie } from './hooks/useBannerCookie'
import { useLocationSearch } from './hooks/useLocationSearch'
import { useLocationManagement } from './hooks/useLocationManagement'
import { shouldOpenOnStartup as shouldOpenOnStartupUtil } from './utils'
import { sortByDistance } from 'utils/sortItems'

type Props = {
  customerInfo: CustomerInfo
  sortingInfo: SortingInfo | undefined
  regions: Region[]
  isProd?: boolean
  queryParams?: {
    [key: string]: string | string[] | undefined
  }
  showOnStartup: boolean
  onCoordinatesSelect: (value: SortingInfo | undefined) => void
  isProductDetails?: boolean
}

const SortButton = ({
  sortingInfo,
  customerInfo,
  regions,
  isProd = false,
  queryParams,
  onCoordinatesSelect,
  showOnStartup,
  isProductDetails = false,
}: Props): JSX.Element => {
  const pathname = usePathname()

  // State
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedOption, setSelectedOption] = useState<SelectOption | undefined>(undefined)

  // Custom hooks
  const bannerCookie = useBannerCookie(isProd, showOnStartup)
  const { searchOptions, searchLocations } = useLocationSearch()
  const { locationLoading, currentLocation, handleUseMyLocationClick, handleRedirect, resetLocationState } =
    useLocationManagement(onCoordinatesSelect, setIsModalOpen)

  // Computed values
  const isLoggedIn = customerInfo.customer !== null

  // Memoized calculations
  const calculatedRegion = useMemo(() => {
    if (!sortingInfo || regions.length === 0) {
      return undefined
    }

    return sortByDistance({ long: sortingInfo.long, lat: sortingInfo.lat }, regions)[0] as Region
  }, [regions, sortingInfo])

  const shouldOpenOnStartup = useMemo((): boolean => {
    return shouldOpenOnStartupUtil(showOnStartup, isProd, bannerCookie, isLoggedIn, sortingInfo)
  }, [showOnStartup, isProd, bannerCookie, isLoggedIn, sortingInfo])

  // Effects
  useEffect(() => {
    setIsModalOpen(shouldOpenOnStartup)
  }, [bannerCookie, queryParams])

  // Event handlers
  const handleModalOpen = useCallback(() => {
    setSelectedOption(undefined)
    resetLocationState()
    setIsModalOpen(true)
  }, [resetLocationState])

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false)
  }, [])

  const handleSearchTermChange = useCallback(
    async (search: string) => {
      await searchLocations(search)
    },
    [searchLocations],
  )

  const handleInputSelectChange = useCallback(
    (value: SelectOption) => {
      setSelectedOption(value)
      handleRedirect(undefined, value)
    },
    [handleRedirect],
  )

  const redirectToLoginPage = useCallback(() => {
    const params = new URLSearchParams()
    params.append('redirectTo', pathname ?? '')
    // TODO: Implement login redirect
    // startTransition(() => {
    //   router.push(`/login?redirectTo=${params.toString()}`)
    // })
  }, [pathname])

  return (
    <>
      <LocationModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        selectedOption={selectedOption}
        searchOptions={searchOptions}
        locationLoading={locationLoading}
        currentLocation={currentLocation}
        isLoggedIn={isLoggedIn}
        onUseMyLocation={handleUseMyLocationClick}
        onSearchTermChange={handleSearchTermChange}
        onInputSelectChange={handleInputSelectChange}
        onRedirectToLogin={redirectToLoginPage}
      />
      <LocationBar
        calculatedRegion={calculatedRegion}
        onOpenModal={handleModalOpen}
        isProductDetails={isProductDetails}
      />
    </>
  )
}

export default SortButton
